import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Loader2, ShieldCheck, AlertTriangle, ChevronDown, ChevronUp, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const PROVIDED_KEYS = ['questura_username', 'questura_password', 'questura_wskey'];

/**
 * Pagina PUBBLICA (no auth) per il proprietario: inserisce le credenziali
 * Questura/ISTAT dei suoi immobili.
 *
 * URL: /owner-portal/:token
 */
const OwnerPortal = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);   // { properties, owner_name, ... }
  const [completed, setCompleted] = useState(false);
  const [expandedProp, setExpandedProp] = useState(null);
  const [verificationResults, setVerificationResults] = useState(null);
  // form state per property: { property_id: { questura_username, ... } }
  const [forms, setForms] = useState({});

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(`${API}/owner-credentials/portal/${token}`);
        if (!mounted) return;
        if (res.data?.completed) {
          setCompleted(true);
          setData(res.data);
        } else {
          setData(res.data);
          // Auto-expand first property
          if (res.data.properties && res.data.properties.length > 0) {
            setExpandedProp(res.data.properties[0].property_id);
          }
        }
      } catch (err) {
        if (!mounted) return;
        const status = err.response?.status;
        if (status === 404) {
          setError('Link non valido o scaduto. Contatta proprietari@holidayw.eu');
        } else {
          setError('Si è verificato un errore. Riprova tra qualche minuto.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [token]);

  const updateForm = (propertyId, field, value) => {
    setForms(prev => ({
      ...prev,
      [propertyId]: { ...(prev[propertyId] || {}), [field]: value },
    }));
  };

  const setDelegated = (propertyId, kind, value) => {
    // kind: 'questura' or 'istat'
    setForms(prev => ({
      ...prev,
      [propertyId]: {
        ...(prev[propertyId] || {}),
        [`${kind}_already_delegated_to_us`]: value,
        // Se delego a HW, resetto i campi
        ...(value && kind === 'questura' ? { questura_username: '', questura_password: '', questura_wskey: '' } : {}),
        ...(value && kind === 'istat' ? { istat_username: '', istat_password: '' } : {}),
      },
    }));
  };

  const propStatus = (prop) => {
    const f = forms[prop.property_id] || {};
    const qOk = !prop.needs_questura
      || f.questura_already_delegated_to_us
      || (f.questura_username && f.questura_password);
    const iOk = !prop.needs_istat
      || f.istat_already_delegated_to_us
      || (f.istat_username && f.istat_password);
    return { qOk, iOk, allOk: qOk && iOk };
  };

  const allReady = () => {
    if (!data?.properties) return false;
    return data.properties.every(p => propStatus(p).allOk);
  };

  const handleSubmit = async () => {
    if (!allReady()) {
      toast.error('Compila i campi richiesti per tutti gli immobili.');
      return;
    }
    setSubmitting(true);
    try {
      const submissions = data.properties.map(p => {
        const f = forms[p.property_id] || {};
        return {
          property_id: p.property_id,
          questura_username: f.questura_username || null,
          questura_password: f.questura_password || null,
          questura_wskey: f.questura_wskey || null,
          questura_already_delegated_to_us: !!f.questura_already_delegated_to_us,
          istat_username: f.istat_username || null,
          istat_password: f.istat_password || null,
          istat_already_delegated_to_us: !!f.istat_already_delegated_to_us,
        };
      });
      const res = await axios.post(`${API}/owner-credentials/portal/${token}/submit`, {
        submissions,
      });
      // Mostra esiti verifica all'utente
      if (res.data?.results) {
        setVerificationResults(res.data.results);
      }
      if (res.data?.completed) {
        setCompleted(true);
        // Riepilogo qualitativo del risultato
        const allVerified = (res.data.results || []).every(r =>
          (r.verifications || []).every(v => v.status === 'verified' || v.status === 'skipped')
        );
        const anyFailed = (res.data.results || []).some(r =>
          (r.verifications || []).some(v => v.status === 'failed')
        );
        if (allVerified) {
          toast.success('Grazie! Credenziali ricevute e verificate con successo.');
        } else if (anyFailed) {
          toast.warning('Salvate, ma alcune credenziali risultano non valide. Le ricontatteremo.');
        } else {
          toast.success('Grazie! Credenziali ricevute. Le verifiche si completeranno entro 24h.');
        }
      } else {
        toast.warning('Alcuni dati sono stati salvati. Completa quelli mancanti.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore durante il salvataggio. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="h-12 w-12 text-amber-500 mx-auto mb-3" />
            <p className="text-slate-700">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (completed) {
    const results = verificationResults || [];
    const verifiedCount = results.filter(r =>
      (r.verifications || []).some(v => v.status === 'verified')
    ).length;
    const failedCount = results.filter(r =>
      (r.verifications || []).some(v => v.status === 'failed')
    ).length;
    const deferredCount = results.filter(r =>
      (r.verifications || []).some(v => v.status === 'deferred' || v.status === 'manual_required')
    ).length;
    const hasFailures = failedCount > 0;

    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-10">
        <Card className="max-w-xl w-full" data-testid="owner-portal-completed">
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                <CheckCircle2 className="h-9 w-9 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2">Tutto fatto, grazie!</h2>
              <p className="text-slate-600">
                Le credenziali sono state ricevute e i Suoi immobili sono ora gestiti automaticamente da HW Smart.
              </p>
            </div>

            {/* Riepilogo verifiche */}
            {results.length > 0 && (
              <div className="space-y-2 my-5">
                {results.map((r, idx) => (
                  <div key={idx} className="bg-slate-50 rounded-lg p-3" data-testid={`verif-prop-${idx}`}>
                    <p className="font-medium text-slate-900 text-sm mb-1">{r.property_name || r.property_id}</p>
                    <ul className="space-y-1">
                      {(r.verifications || []).map((v, vi) => (
                        <li key={vi} className="text-xs flex items-start gap-1.5">
                          {v.status === 'verified' && <span className="text-emerald-600">✅</span>}
                          {v.status === 'failed' && <span className="text-red-600">❌</span>}
                          {v.status === 'deferred' && <span className="text-blue-600">⏳</span>}
                          {v.status === 'manual_required' && <span className="text-amber-600">📋</span>}
                          {v.status === 'skipped' && <span className="text-slate-400">—</span>}
                          <span className="text-slate-700">
                            <strong>{v.kind === 'questura' ? 'Questura' : 'ISTAT'}</strong>: {v.message}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}

            <div className={`p-4 rounded-lg ${hasFailures ? 'bg-amber-50 border border-amber-200' : 'bg-emerald-50 border border-emerald-200'}`}>
              <p className="text-sm font-medium mb-1">
                {hasFailures
                  ? '⚠️ Alcune credenziali necessitano di un controllo'
                  : '✅ Operazione completata correttamente'}
              </p>
              <p className="text-xs text-slate-700">
                {hasFailures
                  ? 'La nostra Customer Care Le scriverà entro 24h per risolvere il problema.'
                  : (deferredCount > 0
                      ? 'Le verifiche delle credenziali ROSS1000 si completeranno entro 24h al primo invio reale.'
                      : 'Tutte le credenziali sono state verificate e funzionano correttamente.')}
              </p>
            </div>

            <p className="text-center text-sm text-slate-500 mt-5">
              Per qualsiasi domanda: <a href="mailto:proprietari@holidayw.eu" className="text-blue-700 underline">proprietari@holidayw.eu</a>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="owner-portal-page">
      <div className="max-w-3xl mx-auto">
        {/* Header con logo HW Smart */}
        <div className="text-center mb-8">
          <img src="/1.png" alt="HW Smart" className="h-16 w-16 mx-auto rounded-full bg-white p-2 shadow" />
          <h1 className="text-2xl font-bold text-slate-900 mt-3">Inserimento credenziali</h1>
          <p className="text-slate-600 mt-1">
            Gentile {data?.owner_name || 'Proprietario'}, completi i campi sotto per i Suoi immobili.
          </p>
        </div>

        {/* Disclaimer trust */}
        <div className="bg-blue-50 border-l-4 border-blue-500 rounded p-4 mb-6 flex items-start gap-3">
          <ShieldCheck className="h-5 w-5 text-blue-700 mt-0.5 shrink-0" />
          <p className="text-sm text-blue-900 leading-relaxed">
            Questa pagina è personale e sicura. I dati viaggiano cifrati e non vengono mai mostrati
            ad altri utenti. Le credenziali servono solo a inviare automaticamente le pratiche
            obbligatorie alla Questura e/o alla Regione.
          </p>
        </div>

        {/* Lista immobili */}
        <div className="space-y-4">
          {(data?.properties || []).map((prop, idx) => {
            const status = propStatus(prop);
            const isExpanded = expandedProp === prop.property_id;
            const f = forms[prop.property_id] || {};
            return (
              <Card key={prop.property_id} className="overflow-hidden" data-testid={`prop-card-${idx}`}>
                <button
                  type="button"
                  className="w-full px-5 py-4 flex items-center justify-between hover:bg-slate-50 text-left"
                  onClick={() => setExpandedProp(isExpanded ? null : prop.property_id)}
                  data-testid={`prop-toggle-${idx}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-slate-900">{prop.property_name}</h3>
                      {prop.needs_questura && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800">Questura</span>
                      )}
                      {prop.needs_istat && (
                        <span className="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800">ISTAT</span>
                      )}
                      {status.allOk && (
                        <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800">
                          <CheckCircle2 className="h-3 w-3" /> Completato
                        </span>
                      )}
                    </div>
                    {prop.region && (
                      <p className="text-xs text-slate-500 mt-1">{prop.region}</p>
                    )}
                  </div>
                  {isExpanded ? <ChevronUp className="h-5 w-5 text-slate-400" /> : <ChevronDown className="h-5 w-5 text-slate-400" />}
                </button>

                {isExpanded && (
                  <CardContent className="border-t border-slate-200 bg-slate-50 space-y-5 pt-5">
                    {/* Sezione Questura */}
                    {prop.needs_questura && (
                      <CredentialSection
                        title="🛡️ Portale Alloggiati Web (Questura)"
                        description='Trova queste credenziali sull’email che la Polizia di Stato Le ha inviato dopo l’accreditamento.'
                        helpLink="https://alloggiatiweb.poliziadistato.it"
                        delegated={!!f.questura_already_delegated_to_us}
                        onDelegateChange={(v) => setDelegated(prop.property_id, 'questura', v)}
                        propertyId={prop.property_id}
                        kindKey="questura"
                        fields={[
                          { key: 'questura_username', label: 'Username Questura', placeholder: 'es. ALG1234567' },
                          { key: 'questura_password', label: 'Password', type: 'password' },
                          { key: 'questura_wskey', label: 'WS Key (opzionale, se rilasciato)', required: false, placeholder: 'es. abcd1234efgh5678' },
                        ]}
                        formValues={f}
                        updateForm={updateForm}
                      />
                    )}

                    {/* Sezione ISTAT */}
                    {prop.needs_istat && (
                      <CredentialSection
                        title="📊 Portale Regionale ISTAT/ROSS1000"
                        description='Le credenziali Le sono state rilasciate dalla Sua Regione (ROSS1000, OTRS Sicilia o portale regionale equivalente).'
                        helpLink={null}
                        delegated={!!f.istat_already_delegated_to_us}
                        onDelegateChange={(v) => setDelegated(prop.property_id, 'istat', v)}
                        propertyId={prop.property_id}
                        kindKey="istat"
                        fields={[
                          { key: 'istat_username', label: 'Username portale regionale', placeholder: 'es. TRS-IT-SIC-00004 o XYZ123' },
                          { key: 'istat_password', label: 'Password', type: 'password' },
                        ]}
                        formValues={f}
                        updateForm={updateForm}
                      />
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>

        {/* Submit */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 items-center justify-between bg-white p-5 rounded-lg shadow-sm">
          <p className="text-sm text-slate-600">
            {allReady()
              ? '✅ Tutto pronto! Clicchi su "Invia" per completare.'
              : 'Per inviare deve completare i dati di TUTTI gli immobili sopra.'}
          </p>
          <Button
            onClick={handleSubmit}
            disabled={!allReady() || submitting}
            className="bg-blue-700 hover:bg-blue-800 px-8"
            data-testid="owner-portal-submit"
          >
            {submitting ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Invio in corso...</>
            ) : (
              <><ShieldCheck className="h-4 w-4 mr-2" /> Invia credenziali</>
            )}
          </Button>
        </div>

        {/* Footer */}
        <p className="text-center text-xs text-slate-500 mt-8">
          HW Smart · <a href="mailto:proprietari@holidayw.eu" className="underline">proprietari@holidayw.eu</a>
        </p>
      </div>
    </div>
  );
};

const CredentialSection = ({
  title, description, helpLink, delegated, onDelegateChange,
  propertyId, kindKey, fields, formValues, updateForm,
}) => (
  <div className="bg-white rounded-lg border border-slate-200 p-4">
    <h4 className="font-semibold text-slate-900 text-base mb-1">{title}</h4>
    <p className="text-xs text-slate-500 mb-3">{description}</p>
    {helpLink && (
      <a href={helpLink} target="_blank" rel="noopener noreferrer"
         className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline mb-3">
        <ExternalLink className="h-3 w-3" /> Vai al portale ufficiale
      </a>
    )}

    {/* Toggle: ho delegato a HW */}
    <label className="flex items-center gap-2 mt-2 mb-3 text-sm cursor-pointer">
      <input
        type="checkbox"
        checked={delegated}
        onChange={(e) => onDelegateChange(e.target.checked)}
        className="rounded"
        data-testid={`delegate-${kindKey}-${propertyId}`}
      />
      <span className="text-slate-700">
        Ho già delegato a HW Smart la richiesta di queste credenziali (non le possiedo io)
      </span>
    </label>

    {/* Form inputs (hidden if delegated) */}
    {!delegated && (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {fields.map((field) => (
          <div key={field.key} className={field.key.includes('wskey') ? 'sm:col-span-2' : ''}>
            <Label className="text-xs">{field.label}{field.required !== false && <span className="text-red-500">*</span>}</Label>
            <Input
              type={field.type || 'text'}
              value={formValues[field.key] || ''}
              onChange={(e) => updateForm(propertyId, field.key, e.target.value)}
              placeholder={field.placeholder || ''}
              data-testid={`field-${field.key}-${propertyId}`}
              autoComplete="off"
            />
          </div>
        ))}
      </div>
    )}
  </div>
);

export default OwnerPortal;
